import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '@/state/store';
import { getChordPlayer } from './player';

const DEFAULT_MAX_MS = 1000;
// A press shorter than this is a tap: the chord sounds as a pianist's own
// near-simultaneous roll. Longer, and the release spreads the notes over the
// time held — the slow arpeggio the comp engine calls the cliché sweep.
const HOLD_MS = 110;
const TAP_ROLL_SECONDS = 0.012;

interface HoldRollOptions {
  maxMs?: number;
  onProgress?: (fraction: number) => void;
}

/**
 * The keyboard's counterpart to useHoldStrum: press-and-hold to roll a stack
 * of pitches through the piano voice. A quick tap plays the stack at the
 * pianist's 12ms spread; a hold rolls it out over the held time on release.
 * Same return shape as useHoldStrum so one button serves both instruments.
 */
export function useHoldRoll(midis: readonly number[], options: HoldRollOptions = {}) {
  const audioEnabled = useStore((s) => s.audioEnabled);
  const maxMs = options.maxMs ?? DEFAULT_MAX_MS;
  const onProgress = options.onProgress;
  const [pressing, setPressing] = useState(false);
  const pressingRef = useRef(false);
  const startRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  // Snapshot at press time so a stack edited mid-hold still plays what was pressed.
  const midisRef = useRef<readonly number[]>(midis);

  const stopRaf = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    stopRaf();
    pressingRef.current = false;
    setPressing(false);
    onProgress?.(0);
  }, [onProgress, stopRaf]);

  useEffect(() => () => stopRaf(), [stopRaf]);

  const begin = useCallback(() => {
    if (!audioEnabled || pressingRef.current) return;
    startRef.current = performance.now();
    midisRef.current = midis;
    pressingRef.current = true;
    setPressing(true);
    onProgress?.(0);
    if (onProgress) {
      const tick = () => {
        onProgress(Math.min(1, (performance.now() - startRef.current) / maxMs));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [audioEnabled, maxMs, midis, onProgress]);

  const cancel = useCallback(() => {
    if (!pressingRef.current) return;
    reset();
  }, [reset]);

  const end = useCallback(() => {
    if (!pressingRef.current) return;
    const held = performance.now() - startRef.current;
    const notes = [...midisRef.current];
    reset();
    if (!audioEnabled || notes.length === 0) return;
    const spread =
      held >= HOLD_MS && notes.length > 1
        ? Math.min(maxMs, held) / 1000 / (notes.length - 1)
        : TAP_ROLL_SECONDS;
    void getChordPlayer().playMidi(notes, spread, 'piano');
  }, [audioEnabled, maxMs, reset]);

  return { pressing, begin, end, cancel };
}
